import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  MobileDrawer,
  MobileDrawerBody,
  theme
} from '@dtx-company/design-system/src'
import { SxProps } from '@mui/material'
import {
  UpsellModalContextProvider,
  useUpsellModalContext,
  useUpsellModalDispatch
} from './UpsellModalContextProvider'
import { UpsellModalProps } from './UpsellModalProps'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import Close from '@mui/icons-material/Close'
import React, { Ref, useCallback } from 'react'

const CloseButton = ({ onClick }: { onClick: () => void }): JSX.Element => {
  const { heapId } = useUpsellModalContext()
  const handleClick = (_e: React.MouseEvent<HTMLButtonElement>): void => {
    fireAnalyticsEvent(`${heapId || 'UpsellModal'}_CloseButton_Clicked`)
    onClick()
  }
  return (
    <Box
      sx={theme => ({
        position: 'absolute',
        right: theme.spacing(2),
        top: theme.spacing(2),
        zIndex: 1
      })}
    >
      <IconButton aria-label="Close" icon={Close} variant="text" onClick={handleClick} />
    </Box>
  )
}

//This logic is a bit confusing, but it's to ensure that the modal is always the same aspect ratio
//and that it doesn't exceed the max width of the screen or the max height of the viewport
//The video will sit in a grid 1/2 of the width so the modal ratio is multiplied by 2
//for the aspectRatio to be respoected the maxWidth and maxHeight are set to 'unset' as well as the height
const ratio = (1280 / 1400) * 2
const maxWidth = theme.breakpoints.values.xl
const maxHeight = `90vh`
const paperProps: SxProps = {
  margin: 0,
  height: 'auto',
  maxHeight: 'unset',
  maxWidth: 'unset',
  aspectRatio: `${ratio}`,
  //take the min of the viewport width - 64px (32px padding on each side) or the maxWidth or respect the maxHeight
  width: `min(calc(100vw - 64px), ${maxWidth}px, calc(${maxHeight} * ${ratio}))`,
  '& .MuiDialogContent-root': {
    overflow: 'hidden',
    padding: '0',
    position: 'relative'
  }
}

type InnerUpsellModalProps = {
  contentRef: Ref<HTMLDivElement>
  onClose: () => void
} & Pick<UpsellModalProps, 'children' | 'open'>

const InnerUpsellModal = ({
  children,
  contentRef,
  onClose,
  open
}: InnerUpsellModalProps): JSX.Element => {
  const { isMobile } = useScreenSizeBreakpoints()
  const { ariaDescribedById, ariaLabelledById } = useUpsellModalContext()
  const dispatch = useUpsellModalDispatch()
  const handleClose = (): void => {
    onClose()
    dispatch({ type: 'RESET_STEP' })
  }
  const content = (
    <>
      <CloseButton onClick={handleClose} />
      {children}
    </>
  )
  return isMobile ? (
    <MobileDrawer open={open} PaperProps={{ sx: { height: '80%', pt: 0 } }} onClose={handleClose}>
      <MobileDrawerBody
        ref={contentRef}
        sx={{
          display: 'flex',
          position: 'relative',
          flex: '1',
          flexDirection: 'column',
          p: 0,
          pt: theme.spacing(4)
        }}
      >
        {content}
      </MobileDrawerBody>
    </MobileDrawer>
  ) : (
    <Dialog
      open={open}
      PaperProps={{ sx: { ...paperProps } }}
      aria-describedby={ariaDescribedById}
      aria-labelledby={ariaLabelledById}
    >
      <DialogContent ref={contentRef}>{content}</DialogContent>
    </Dialog>
  )
}

/**
 * This is an abstraction of the PlanPurchaseExperience component to be used in-page
 * for upsells.
 *
 * @see LimitUpsellModal
 */
export const UpsellModal = React.forwardRef<HTMLDivElement, UpsellModalProps>((props, ref) => {
  const { children, heapId, open, onClose } = props

  const handleClose = useCallback(() => {
    onClose('dismiss')
  }, [onClose])
  const value = { heapId }

  return (
    <UpsellModalContextProvider value={value}>
      <InnerUpsellModal open={open} contentRef={ref} onClose={handleClose}>
        {children}
      </InnerUpsellModal>
    </UpsellModalContextProvider>
  )
})

UpsellModal.displayName = 'UpsellModal'
